import React from 'react';
import {PropTypes} from 'prop-types';
import {connect} from 'react-redux';
import moment from 'moment';
import {get, isEqual} from 'lodash';
import {SecurityHelper} from 'sarsaparilla';
import { bindActionCreators } from 'redux';
import DetailedAvailabilityNavigator from 'ui-permit/shared-components/detailed-availability/DetailedAvailabilityNavigator';

import OvernightAvailabilityGrid from '../components/OvernightAvailabilityGrid';
import BookNowComponent from '../components/BookNowComponent';
import * as aActions from '../actions/availability';
import * as pActions from '../actions/permit';
import * as types from '../constants';
import {getPermitTypeInyo, INYO_COMMERCIAL_TRAILS, OVERNIGHT_PERMIT} from '../utils/inyoUtils';

const propTypes = {
  permitId: PropTypes.string,
  permitDate: PropTypes.string,
  permit: PropTypes.object,
  availabilities: PropTypes.object,
  divisions: PropTypes.object,
  selectedStartDate: PropTypes.string,
  selectedDivision: PropTypes.object,
  divisionSearchCriteria: PropTypes.object,
  selectedGroupSize: PropTypes.number,
  onBook: PropTypes.func.isRequired,
  contactAgency: PropTypes.string,
  postInProgress: PropTypes.bool,
  existingIssuanceId: PropTypes.string,
  aggSeasonRules: PropTypes.array,
  waitingForUserAccount: PropTypes.bool,
  daysToSkip: PropTypes.number.isRequired,
  numOfCols: PropTypes.number,
  isSmallerGrid: PropTypes.bool,
  pActions: PropTypes.shape({
    setPermitDate: PropTypes.func,
    setSelectedStartDate: PropTypes.func,
    setSelectedDivision: PropTypes.func,
    setSelectedEndDate: PropTypes.func,
    setQuestionnaireAnswers: PropTypes.func,
  }),
  aActions: PropTypes.shape({
    fetchAvailabilityAllDivisions: PropTypes.func,
  }),
  onDivisionDetailsClick: PropTypes.func,
  isFullView:  PropTypes.bool,
};

const defaultProps = {
  postInProgress: false,
  waitingForUserAccount: false
};

export class AvailabilityEnterInyoContainer extends React.Component {
  static displayName = 'AvailabilityEnterInyoContainer';

  constructor(props) {
    super(props);
    this.state = {
      permitId: props.permitId,
      permitDate: props.permitDate,
      divisions: this.getFilteredDivisions(props.divisions),
      monthsFetched: []
    };
    this.onNavigatorDateChanged = this.onNavigatorDateChanged.bind(this);
    this.handleNextAvailableClick = this.handleNextAvailableClick.bind(this);
    this.onSelectToggle = this.onSelectToggle.bind(this);
    this.onClearDates = this.onClearDates.bind(this);
    this.handleIsCommercialGuided = this.handleIsCommercialGuided.bind(this);
    this.onBook = this.onBook.bind(this);
  }

  componentDidMount() {
    const existingIssuanceId = get(this.props, 'issuance.id');
    this.refreshAvailability(existingIssuanceId);
  }

  componentDidUpdate(prevProps) {
    const prevIssuanceId = get(prevProps, 'issuance.id');
    const existingIssuanceId = get(this.props, 'issuance.id');
    if (prevIssuanceId !== existingIssuanceId) {
      this.refreshAvailability(existingIssuanceId);
    }
    if (this.props.permitDate !== this.state.permitDate
      || this.props.permitId !== this.state.permitId
      || !isEqual(this.props.divisions, this.props.divisions)
      || !isEqual(this.props.divisionSearchCriteria, this.props.divisionSearchCriteria)) {
        this.setState({
        permitId: this.props.permitId,
        permitDate: this.props.permitDate,
        divisions: this.getFilteredDivisions(this.props.divisions, this.props.divisionSearchCriteria)
      }, () => {
        this.refreshAvailability(existingIssuanceId);
      });
    }
  }

  onNavigatorDateChanged(dates) {
    this.props.pActions.setPermitDate(dates.startDate.format(types.AVAILABILITY_KEY_MAP_FORMAT));
  }

  onSelectToggle(division, permitDate) {
    if (this.props.selectedStartDate === permitDate &&
      this.props.selectedDivision &&
      this.props.selectedDivision.id === division.id) {
      this.props.pActions.setSelectedStartDate(null);
    }
 else {
      this.props.pActions.setSelectedStartDate(permitDate);
    }
    this.props.pActions.setSelectedDivision(division);
    this.props.pActions.setSelectedEndDate(null);
  }

  onClearDates() {
    this.props.pActions.setSelectedStartDate(null);
    this.props.pActions.setSelectedEndDate(null);
  }

  onBook() {
    return this.props.onBook(getPermitTypeInyo(OVERNIGHT_PERMIT));
  }

  getFilteredDivisions(divisionObjs, searchCriteria) {
    const ret = [];
    Object.keys(divisionObjs).forEach((id) => {
      const division = divisionObjs[id];
      if (division.type === types.DIV_TYPE_EP) {
        if (SecurityHelper.isCommercialAccount()) {
          ret[ret.length] = divisionObjs[id];
        }
        else if (!INYO_COMMERCIAL_TRAILS.includes(id)) {
          ret[ret.length] = divisionObjs[id];
        }
      }
    });

    return this.applySearchCriteria(ret, searchCriteria);
  }

  applySearchCriteria(divisionObjs, searchCriteria) {
    let ret = divisionObjs;
    if (searchCriteria) {
      if (searchCriteria.searchText && searchCriteria.searchText !== '') {
        const matcher = new RegExp(searchCriteria.searchText, 'i');
        ret = ret.filter((item) => {
          return matcher.test(`${item.code} ${item.name}  ${item.district}`);
        });
      }
      if (searchCriteria.areas.length > 0) {
        const matcher = new RegExp(searchCriteria.areas.join('|'), 'i');
        ret = ret.filter((item) => {
          return matcher.test(item.district);
        });
      }
    }
    return ret;
  }

  handleNextAvailableClick() {
    const permitDate = moment(get(this.props, "availabilities.next_available_date")).utc();
    this.props.pActions.setPermitDate(permitDate.format(types.AVAILABILITY_KEY_MAP_FORMAT));
  }

  refreshAvailability(issuanceId) {
    const startDate = moment.utc(this.state.permitDate);
    let nextMonth
    if (this.isEndOfMonth(startDate)) {
      nextMonth = startDate.clone().utc().add(1, 'month').startOf('month')
    }
    if ((!this.isEndOfMonth(startDate) && this.state.monthsFetched.includes(startDate.format('YYYY-MM'))
      || (!this.isEndOfMonth(startDate) && nextMonth && this.state.monthsFetched.includes(nextMonth.format('YYYY-MM')))
      || !this.state.permitId)) {
      return;
    }

    this.setState({
      monthsFetched: [
        ...this.state.monthsFetched,
        startDate
          .clone()
          .startOf('month')
          .format('YYYY-MM')
      ]
    })

    if (this.state.permitId && this.state.permitDate && !this.state.monthsFetched.includes(startDate.format('YYYY-MM'))) {
      this.props.aActions.fetchAvailabilityAllDivisions(
        this.state.permitId,
        startDate,
        startDate.clone().utc().startOf('month').toISOString(),
        SecurityHelper.isCommercialAccount(),
        issuanceId
      ).then(() => {
        if (nextMonth && !this.state.monthsFetched.includes(nextMonth.format('YYYY-MM'))) {
          this.props.aActions.fetchAvailabilityAllDivisions(
            this.state.permitId,
            nextMonth,
            nextMonth.clone().utc().startOf('month').toISOString(),
            SecurityHelper.isCommercialAccount(),
            issuanceId
          ).then(() => {
            this.setState({
              monthsFetched: [
                ...this.state.monthsFetched,
                nextMonth
                  .clone()
                  .startOf('month')
                  .format('YYYY-MM')
              ]
            })
          })
        }
      });
    }
 else if (!this.state.monthsFetched.includes(nextMonth.format('YYYY-MM'))) {
      this.props.aActions.fetchAvailabilityAllDivisions(
        this.state.permitId,
        nextMonth,
        nextMonth.clone().utc().startOf('month').toISOString(),
        SecurityHelper.isCommercialAccount(),
        issuanceId
      ).then(() => {
        this.setState({
          monthsFetched: [
            ...this.state.monthsFetched,
            nextMonth
              .clone()
              .startOf('month')
              .format('YYYY-MM')
          ]
        })
      })
    }
  }

  // check if start date is a day where two months will be displayed in grid
  isEndOfMonth = (date) => {
    const endOfMonth = date.clone().utc().endOf('month')
    const daysBeforeEndOfMonth = date.clone().utc().endOf('month').subtract(5, 'day')
    return date.isBetween(daysBeforeEndOfMonth, endOfMonth)
  }

  handleIsCommercialGuided(event) {
    const { value } = event.target;
    this.props.pActions.setQuestionnaireAnswers({ contactAgency: value });
  }

  isPrevButtonDisabled = () => moment().startOf('day').toJSON() === moment(this.props.permitDate).toJSON();

  skip = (duration, temporalUnit) => {
    const startDate = moment(this.props.permitDate);
    let newStartDate = startDate.clone().add(duration, temporalUnit);
    let newEndDate = startDate.clone().add(this.props.daysToSkip, 'day').add(duration, temporalUnit);
    if (newStartDate.isBefore(moment().utc().startOf('day'))) {
      newStartDate = moment().utc().startOf('day');
      newEndDate = newStartDate.clone().add(duration, temporalUnit);
    }
    this.onNavigatorDateChanged({ startDate: newStartDate, endDate: newEndDate });
  }

  render() {
    const {
      contactAgency,
      selectedDivision,
      selectedStartDate,
      postInProgress,
      waitingForUserAccount,
      permitDate,
      daysToSkip,
      isFullView,
    } = this.props;
    
    const isEnabled = selectedDivision !== null && selectedStartDate !== null && !postInProgress && !waitingForUserAccount;
    return (
      <div className="per-availability-content">
        {(contactAgency === 'no' || SecurityHelper.isCommercialAccount()) &&
          <div>
            <DetailedAvailabilityNavigator
              onPrev={() => this.skip(-daysToSkip, 'days')}
              onNext={() => this.skip(daysToSkip, 'days')}
              onNextAvailable={this.handleNextAvailableClick}
              prevText={`Prev ${daysToSkip} Days`}
              nextText={`Next ${daysToSkip} Days`}
              onClearSelection={this.onClearDates}
              startDate={moment.utc(permitDate)}
              endDate={moment.utc(permitDate).add(daysToSkip, 'day')}
              isPrevDisabled={this.isPrevButtonDisabled()}
            />

            <OvernightAvailabilityGrid
              selectedGroupSize={this.props.selectedGroupSize}
              divisions={this.state.divisions}
              availabilities={this.props.availabilities}
              permitDate={this.props.permitDate}
              permit={this.props.permit}
              onSelectToggle={this.onSelectToggle}
              selectedDivision={this.props.selectedDivision}
              selectedStartDate={this.props.selectedStartDate}
              aggSeasonRules={this.props.aggSeasonRules}
              numOfCols={this.props.numOfCols}
              isSmallerGrid={this.props.isSmallerGrid}
              isFullView={isFullView}
              onDivisionDetailsClick={this.props.onDivisionDetailsClick}
            />

            <BookNowComponent
              selectedDivision={this.props.selectedDivision}
              selectedStartDate={this.props.selectedStartDate}
              onBookClick={this.onBook}
              isEnabled={isEnabled} />
          </div>
        }
      </div>
    );
  }
}

AvailabilityEnterInyoContainer.propTypes = propTypes;
AvailabilityEnterInyoContainer.defaultProps = defaultProps;

function select(state) {
  return {
    permitId: state.inyonwhitney.permit.result.id,
    permitDate: state.inyonwhitney.permitDate,
    selectedDivision: state.inyonwhitney.selectedDivision,
    selectedStartDate: state.inyonwhitney.selectedStartDate,
    permit: state.inyonwhitney.permit.result,
    availabilities: state.inyonwhitney.permit.availabilities,
    divisions: state.inyonwhitney.permit.result.divisions,
    issuance: state.inyonwhitney.permit.issuance,
    divisionSearchCriteria: state.inyonwhitney.divisionSearchCriteria,
    contactAgency: state.inyonwhitney.questionnaire.contactAgency,
    postInProgress: state.inyonwhitney.permit.issuance.post_in_progress,
    aggSeasonRules: state.inyonwhitney.agg_season_rules
  };
}

function mapDispatchToProps(dispatch) {
  return {
    pActions: bindActionCreators(pActions, dispatch),
    aActions: bindActionCreators(aActions, dispatch),
  };
}

export default connect(select, mapDispatchToProps)(AvailabilityEnterInyoContainer);
